import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  /**
   * Every variant of the product, prefetched once so a variant change can show its price before
   * the section re-render arrives.
   * @type {{ options: { name: string }[], variants: InstantVariant[] } | undefined}
   */
  #product;

  /** @type {AbortController | undefined} */
  #productRequest;

  connectedCallback() {
    super.connectedCallback();
    this.#prefetchProduct();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#productRequest?.abort();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Handles product select event and updates the price.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    this.#renderInstantPrice(event);

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const { priceContainer, volumePricingNote } = this.refs;
        // Find the new product-price element in the updated HTML
        const newProductPrice = html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
        if (!newProductPrice) return;

        // Update price container
        const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
        if (newPrice && priceContainer) {
          priceContainer.replaceWith(newPrice);
        }

        // Update volume pricing note
        const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
        if (!newNote) {
          volumePricingNote?.remove();
        } else if (!volumePricingNote) {
          // Use newPrice since priceContainer was just replaced and now points to the detached element
          newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
        } else {
          volumePricingNote.replaceWith(newNote);
        }

        // Update installments (SPI banner) variant ID to trigger payment terms re-render
        const installmentsInput = /** @type {HTMLInputElement|null} */ (
          this.querySelector(`#product-form-installment-${this.dataset.blockId} input[name="id"]`)
        );
        if (installmentsInput) {
          installmentsInput.value = detail.resource?.id ?? '';
          installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-price] Event promise rejected:', error);
      });
  };

  /**
   * @typedef {object} InstantVariant
   * @property {string[]} options - Option values, in the product's option order.
   * @property {number} price - In minor units.
   * @property {number | null} compare_at_price - In minor units.
   * @property {number} [unit_price]
   * @property {unknown[]} [quantity_price_breaks]
   */

  /**
   * Fetches the product's variants once, when the block opts in through `data-product-json-url`.
   */
  async #prefetchProduct() {
    const { productJsonUrl } = this.dataset;
    if (!productJsonUrl || this.#product) return;

    this.#productRequest?.abort();
    this.#productRequest = new AbortController();

    try {
      const response = await fetch(productJsonUrl, { signal: this.#productRequest.signal });
      if (!response.ok) return;
      const product = await response.json();
      if (Array.isArray(product?.options) && Array.isArray(product?.variants)) this.#product = product;
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[product-price] Variant prefetch failed:', error);
    }
  }

  /**
   * Shows the selected variant's price straight away, from the prefetched variants. Anything this
   * does not cover (volume pricing, unit prices, combined listings, an unmatched combination, the
   * prefetch still pending) is left to the section re-render, which replaces the price regardless.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #renderInstantPrice(event) {
    const product = this.#product;
    const { priceContainer } = this.refs;
    const { moneyFormat, currency, productHandle } = this.dataset;
    if (!product || !priceContainer || !moneyFormat || !currency) return;
    if (event.product?.handle !== productHandle) return;

    const connectedProductUrl = event.detail?.connectedProductUrl;
    if (typeof connectedProductUrl === 'string' && connectedProductUrl !== '') return;

    const selected = new Map((event.selectedOptions ?? []).map(({ name, value }) => [name, value]));
    if (selected.size !== product.options.length) return;

    const variant = product.variants.find((candidate) =>
      product.options.every((option, index) => candidate.options?.[index] === selected.get(option.name))
    );
    // A combination with no variant (e.g. a frame not made at this size) renders with no price
    if (!variant) {
      priceContainer.replaceChildren(' ');
      return;
    }
    if (typeof variant.price !== 'number') return;
    if (variant.quantity_price_breaks?.length || variant.unit_price != null) return;

    const price = this.#formatPrice(variant.price, moneyFormat, currency);
    const compareAtPrice = variant.compare_at_price ?? 0;

    if (compareAtPrice > variant.price) {
      const sale = this.#priceGroup('price-item__group price', this.dataset.labelSale, price);
      const regular = this.#priceGroup(
        'price-item__group price-item--regular compare-at-price',
        this.dataset.labelRegular,
        this.#formatPrice(compareAtPrice, moneyFormat, currency)
      );
      const groups = this.dataset.salePriceFirst === 'true' ? [sale, regular] : [regular, sale];
      priceContainer.replaceChildren(groups[0], ' ', groups[1]);
    } else {
      const span = document.createElement('span');
      span.className = 'price';
      span.textContent = price;
      priceContainer.replaceChildren(span);
    }
  }

  /**
   * Formats a price the way the `price` snippet does: whole amounts without ".00" when the block asks
   * for it (Liquid's money_without_trailing_zeros), otherwise in the format as given.
   * @param {number} amount - In minor units.
   * @param {string} format
   * @param {string} currency
   * @returns {string}
   */
  #formatPrice(amount, format, currency) {
    const trim = this.dataset.trimZeros === 'true' && amount % 100 === 0;
    return formatMoney(amount, trim ? format.replace(/{{\s*amount\s*}}/, '{{amount_no_decimals}}') : format, currency);
  }

  /**
   * Builds one price group, matching the markup of the `price` snippet.
   * @param {string} className
   * @param {string | undefined} label - Screen-reader label.
   * @param {string} amount - Formatted price.
   * @returns {HTMLSpanElement}
   */
  #priceGroup(className, label, amount) {
    const group = document.createElement('span');
    group.className = className;
    const hidden = document.createElement('span');
    hidden.className = 'visually-hidden';
    hidden.textContent = `${label ?? ''}\u00a0`;
    group.append(hidden, amount);
    return group;
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
